import type { DiscoverItem, Trip, TripDay, TripStop } from "../types";
import { generateId } from "./id";
import { styleFromVibes } from "../state/trip-context";
import type { ReadyTrip } from "./ready-trips-data";

export { READY_TRIPS, type ReadyTrip } from "./ready-trips-data";

/** The catalogue rows a ready trip would use, day by day; missing slugs dropped. */
export function resolveReadyDays(
  ready: ReadyTrip,
  bySlug: (slug: string) => DiscoverItem | null,
): DiscoverItem[][] {
  return ready.days.map((slugs) =>
    slugs.map(bySlug).filter((item): item is DiscoverItem => item !== null),
  );
}

/** Whether enough of a ready trip is in the catalogue to be worth offering. */
export function isReadyTripAvailable(
  ready: ReadyTrip,
  bySlug: (slug: string) => DiscoverItem | null,
): boolean {
  return resolveReadyDays(ready, bySlug).some((day) => day.length > 0);
}

/**
 * A new trip copied from a ready one: the guest's own, with fresh ids, so
 * editing it never touches the template or another copy.
 *
 * Stops carry a snapshot of the venue, the same as a stop added by hand, so
 * the copy still reads as the places it was made from if the catalogue is slow.
 */
export function buildTripFromReady(
  ready: ReadyTrip,
  bySlug: (slug: string) => DiscoverItem | null,
  input: { name: string; dayLabel: (n: number) => string; startDate: string; people: number },
): Trip {
  const resolved = resolveReadyDays(ready, bySlug);
  const days: TripDay[] = resolved.map((items, index) => ({
    id: generateId("day"),
    label: input.dayLabel(index + 1),
    stops: items.map<TripStop>((item) => ({
      id: generateId("stop"),
      itemId: item.id,
      snapshot: {
        title: item.title,
        image: item.image,
        kind: item.subtitle || item.pinLabel || null,
      },
    })),
  }));
  return {
    id: generateId("trip"),
    name: input.name,
    city: ready.city,
    style: styleFromVibes(ready.vibes),
    createdAt: new Date().toISOString(),
    days,
    vibes: ready.vibes,
    startDate: input.startDate,
    people: input.people,
  };
}

/** The first photograph a ready trip would show, for its card. */
export function readyTripCover(
  ready: ReadyTrip,
  bySlug: (slug: string) => DiscoverItem | null,
): string | null {
  for (const day of resolveReadyDays(ready, bySlug)) {
    for (const item of day) if (item.image) return item.image;
  }
  return null;
}
